import React, { useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import { Form, Field } from 'react-final-form'
import { Button, Input, Alert } from 'element-react'
import queryString from 'query-string'
import { withRouter } from 'react-router-dom'
import CustomLoader from '../../../components/user/includes/CustomLoader'
import {
    padIdWithZeroes,
    tagHotjarRecording,
    createValidator,
} from '../../../helpers/util'
import {
    UnlabeledInputField,
    CustomCheckbox,
} from '../../../helpers/fieldHelper'
import ErrorAlert from '../../../components/views/includes/ErrorAlert'
import useAuth from '../../../hooks/useAuth'
import useAdmin from '../../../hooks/useAdmin'
import AppRoutes from '../../../routes/appRoutes'

const validate = createValidator(['firstName', 'lastName', 'email'])

const FormType = {
    SAP_SEARCH: 'SAP_Search',
    ADD_USER: 'Add_User',
}

const AddUserForm = props => {
    // The values required for the axios call to /ajax/register
    // should probably be separated out into a single state object at some point
    // with useReducer to control how they're set and updated.
    const [sapId, setSapId] = useState('')
    const [companyId, setCompanyId] = useState('')
    const [customer, setCustomer] = useState('')
    const [failedAccounts, setFailedAccounts] = useState([])

    // State management for SAP ID search
    const [loading, setLoading] = useState(false)
    const [error, setError] = useState('')
    const [currentForm, setCurrentForm] = useState(FormType.SAP_SEARCH)
    const { addNewUser } = useAuth()
    const { verifyOneCustomer } = useAdmin()

    const {
        location: { search },
    } = props

    useEffect(() => {
        const values = queryString.parse(search)
        const passedSapId = values.sapId ? values.sapId : ''
        setSapId(padIdWithZeroes(passedSapId))
    }, [search])

    const updateSap = val => {
        setSapId(val)
    }

    const handleUserAddedSuccess = () => {
        setError('')
        props.history.push(AppRoutes.adminSearch(`?company=${companyId}`))
    }

    const handleErrorAddUser = err => {
        tagHotjarRecording('API Error')
        let errs = err.response.data.errors
        let message = err.response.data.message
        if (errs) {
            if (errs.email && errs.email[0].length > 0) {
                setError(errs.email[0])
            } else if (errs.firstName && errs.firstName[0].length > 0) {
                setError(errs.firstName[0])
            } else if (errs.lastName[0].length > 0) {
                setError(errs.lastName[0])
            }
        } else {
            setError(message)
        }
    }

    const onSubmit = userInfo => {
        // This route (ajax/register) is built into Laravel and triggers this function:
        // App/Http/Controllers/Auth/RegsiterController->register(). (95% sure on that)
        const formatData = {
            sapId: padIdWithZeroes(sapId),
            source: 'admin',
            companyId,
            customer,
            ...userInfo,
        }
        addNewUser(formatData, handleUserAddedSuccess, handleErrorAddUser)
    }

    const handleKeyPress = e => {
        if (e.charCode == 13) {
            e.preventDefault()
            e.stopPropagation()
            handleSearch(e)
        }
    }

    const handleSuccessVerifyCustomer = response => {
        setFailedAccounts(response.data.failed_accounts)
        setCustomer(response.data.customer.name)
        setSapId(response.data.customer.sap_id)
        setCompanyId(response.data.customer.id)
        setError('')
        setLoading(false)
        setCurrentForm(FormType.ADD_USER)
    }

    const handleErrorVerifyCustomer = err => {
        let errs = err.response.data.message
        setError(errs)
        setLoading(false)
    }

    const handleSearch = e => {
        e.preventDefault()
        setError('')
        setLoading(true)

        verifyOneCustomer(
            padIdWithZeroes(sapId),
            handleSuccessVerifyCustomer,
            handleErrorVerifyCustomer,
        )
    }

    const reset = () => {
        setCurrentForm(FormType.SAP_SEARCH)
    }

    return (
        <div>
            <h4>Add Single User</h4>
            <p>
                Search for the customer account to be associated with the new
                user
            </p>

            {error && <ErrorAlert error={error} />}

            {failedAccounts &&
                failedAccounts.map(account => {
                    return (
                        <Alert
                            key={account?.salesforceID}
                            className="m-2"
                            type="warning"
                            title="Warning - Error synced location"
                            description={
                                account?.name + ' - ' + account?.salesforceID
                            }
                            showIcon
                        />
                    )
                })}

            {currentForm === FormType.SAP_SEARCH ? (
                <div>
                    <Input
                        label="SAP ID"
                        name="sapId"
                        type="text"
                        value={sapId}
                        placeholder="Search by SAP ID"
                        className="search el-append-button"
                        onChange={updateSap}
                        onKeyPress={handleKeyPress}
                        append={
                            <Button type="default" onClick={handleSearch}>
                                Search
                            </Button>
                        }
                    />

                    {loading && (
                        <div className="demo-loading padding-top-large padding-bottom-large">
                            <CustomLoader text="Loading" />
                        </div>
                    )}
                </div>
            ) : null}

            {currentForm === FormType.ADD_USER ? (
                <Form validate={validate} onSubmit={onSubmit}>
                    {({ handleSubmit, pristine, submitting }) => (
                        <form id="addUserForm" onSubmit={handleSubmit}>
                            {customer.length ? (
                                <React.Fragment>
                                    <h6 className="secondary-color margin-bottom-none">
                                        SAP ID: {sapId}
                                    </h6>
                                    <h6 className="border-bottom">
                                        {customer}
                                    </h6>
                                </React.Fragment>
                            ) : null}

                            <div className="row">
                                <div className="columns medium-6">
                                    <Field
                                        label="First Name"
                                        name="firstName"
                                        component={UnlabeledInputField}
                                        type="text"
                                    />
                                </div>
                                <div className="columns medium-6">
                                    <Field
                                        label="Last Name"
                                        name="lastName"
                                        component={UnlabeledInputField}
                                        type="text"
                                    />
                                </div>
                            </div>
                            <div className="row">
                                <div className="columns medium-6">
                                    <Field
                                        label="Email Address"
                                        name="email"
                                        component={UnlabeledInputField}
                                        type="text"
                                    />
                                </div>
                                <div className="columns medium-6">
                                    <Field
                                        label="This user is a Customer Account Manager"
                                        name="manager"
                                        id="manager"
                                        component={CustomCheckbox}
                                        type="checkbox"
                                    />
                                </div>
                            </div>

                            <div className="row">
                                <div className="columns medium-6 medium-push-6">
                                    <div className="row">
                                        <div className="columns medium-6">
                                            <Button
                                                className="secondary expanded"
                                                onClick={reset}
                                            >
                                                Cancel
                                            </Button>
                                        </div>
                                        <div className="columns medium-6">
                                            <Button
                                                nativeType="submit"
                                                type="primary"
                                                className="expanded"
                                                disabled={
                                                    pristine || submitting
                                                }
                                            >
                                                Create
                                            </Button>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </form>
                    )}
                </Form>
            ) : null}
        </div>
    )
}

AddUserForm.propTypes = {
    history: PropTypes.shape({
        push: PropTypes.func,
    }),
    location: PropTypes.shape({
        search: PropTypes.string,
    }),
}

const routedAddUserForm = withRouter(AddUserForm)

export default routedAddUserForm
